<script setup>
import { h, defineProps, computed } from "vue"
import Sidebar from "./Sidebar.vue"
import RichTextRenderer from "contentful-rich-text-vue-renderer"
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import FAQ from "@common/js/components/FAQ.vue"
import RelatedContent from "./RelatedContent.vue"
import Hero from "./Hero.vue"
import ImageLinks from "./ImageLinks.vue"
import CTAFindInstaller from "@/js/partials/CTAFindInstaller.vue"

const props = defineProps({
  entry: {
    required: true,
    type: Object,
  },
})

function renderMarks() {
  return {
    [MARKS.BOLD]: (text, key) =>
      h("strong", { class: "font-medium text-gray-900", key }, text),
    [MARKS.ITALIC]: (text, key) => h("em", { class: "italic", key }, text),
  }
}

function renderNodes() {
  return {
    [BLOCKS.PARAGRAPH]: (node, key, next) =>
      h(
        "p",
        { class: "text-lg text-gray-600 mb-8", key },
        next(node.content, key, next)
      ),
    [BLOCKS.HEADING_1]: (node, key, next) =>
      h(
        "h1", // need to attach an id tag here
        {
          class: "h1 text-gray-900 mb-4",
          key,
        },
        next(node.content, key, next)
      ),
    [BLOCKS.HEADING_2]: (node, key, next) =>
      h(
        "h2", // need to attach an id tag here
        {
          class: "h2 text-gray-900 mb-4",
          key,
        },
        next(node.content, key, next)
      ),
    [BLOCKS.HEADING_3]: (node, key, next) =>
      h(
        "h3", // need to attach an id tag here
        {
          class: "h3 text-gray-900 mb-4",
          style: "scroll-margin-top: 100px",
          key,
        },
        next(node.content, key, next)
      ),
    [BLOCKS.HEADING_4]: (node, key, next) =>
      h(
        "h4",
        {
          class: "h4 text-gray-900 mb-4",
          key,
        },
        next(node.content, key, next)
      ),
    [BLOCKS.QUOTE]: (node, key, next) =>
      h(
        "blockquote",
        {
          class: "italic text-gray-900 font-medium mb-8",
          key,
        },
        next(node.content, key, next)
      ),
    [BLOCKS.EMBEDDED_ASSET]: (node, key, next) => {
      const file = node.data.target.fields.file
      const title = node.data.target.fields.title
      if (file.contentType.includes("video")) {
        return h(
          "vue-plyr",
          {
            options: {},
            key,
            class: "mt-2 mb-8",
          },
          [
            h(
              "video",
              { controls: true, crossorigin: true, playsinline: true },
              [
                h("source", {
                  src: `https://${file.url}`,
                  type: file.contentType,
                }),
              ]
            ),
          ]
        )
      } else if (file.contentType.includes("pdf")) {
        return h(
          "object",
          {
            data: `https://${file.url}`,
            type: "application/pdf",
            style: "width: 100%;height:700px",
          },
          next(node.content, key, next)
        )
      } else {
        return h(
          "img",
          {
            src: `https://${file.url}`,
            class: "mb-5",
            alt: title,
          },
          next(node.content, key, next)
        )
      }
    },
    [BLOCKS.UL_LIST]: (node, key, next) =>
      h(
        "ul",
        {
          class: "list-disc pl-5  space-y-2",
          key,
        },
        next(node.content, key, next)
      ),
    [BLOCKS.LIST_ITEM]: (node, key, next) =>
      h(
        "li",
        {
          key,
          class: "mb-1",
        },
        next(node.content, key, next)
      ),

    [BLOCKS.OL_LIST]: (node, key, next) =>
      h(
        "ol",
        {
          class: "list-decimal pl-5 space-y-2",
          key,
        },
        next(node.content, key, next)
      ),
    [BLOCKS.TABLE]: (node, key, next) =>
      h(
        "table",
        {
          key,
          class:
            "table-auto w-full my-2 border-b border-slate-200 dark:border-slate-800",
        },
        next(node.content, key, next)
      ),
    [BLOCKS.TABLE_HEADER_CELL]: (node, key, next) =>
      h(
        "th",
        {
          key,
          class:
            "text-left text-slate-800 dark:text-slate-200 font-medium px-2 first:pl-0 last:pr-0 py-3",
        },
        next(node.content, key, next)
      ),
    [BLOCKS.TABLE_ROW]: (node, key, next) =>
      h(
        "tr",
        {
          key,
          class: "border-t border-slate-200 dark:border-slate-800",
        },
        next(node.content, key, next)
      ),
    [BLOCKS.TABLE_CELL]: (node, key, next) =>
      h(
        "td",
        {
          key,
          class: "px-2 first:pl-0 last:pr-0 py-3",
        },
        next(node.content, key, next)
      ),
    [INLINES.HYPERLINK]: (node, key, next) =>
      h(
        "a",
        {
          class: "font-medium text-gray-900 hover:underline",
          href: node.data.uri,
          key,
        },
        next(node.content, key, next)
      ),
  }
}
// computed
const seriesTag = computed(() => {
  const entryTags = props.entry.metadata.tags.map((o) => o.sys.id)

  if (entryTags.includes("installationSteps"))
    return {
      title: "Comment faire ?",
      to: { name: "BlogSeries", query: { series: "installationSteps" } },
    }
  else if (entryTags.includes("introductions"))
    return {
      title: "Guide solaire",
      to: { name: "BlogSeries", query: { series: "introductions" } },
    }
  else if (entryTags.includes("stories"))
    return {
      title: "Témoignages",
      to: { name: "BlogSeries", query: { series: "stories" } },
    }
  else
    return {
      title: "Articles",
      to: { name: "BlogSeries" },
    }
})
</script>

<template>
  <section>
    <!-- Hero -->
    <Hero :entry="entry" />
    <div class="max-w-7xl mx-auto space-y-12">
      <!-- Main Content & Sidebar Layout -->
      <div class="lg:flex lg:space-x-8 py-6">
        <!-- Main Content (left side) -->
        <div class="flex-1 main-content">
          <div
            v-for="block in entry.fields.blocks"
            :id="block.fields.id"
            :key="block.sys.id"
            class="w-full md:w-5/6"
          >
            <RichTextRenderer
              :document="block.fields.body"
              :node-renderers="renderNodes()"
              :mark-renderers="renderMarks()"
            />
          </div>
        </div>

        <!-- Sidebar (right side) -->
        <Sidebar :entry="entry" />
      </div>
      <!-- CTAFindInstaller -->
      <CTAFindInstaller
        class="ring ring-1 ring-gray-200 rounded-lg p-6 shadow"
      />
      <!-- Image Links -->
      <ImageLinks />
      <!-- FAQ Section -->
      <FAQ v-if="entry.fields.faq" :entry="entry.fields.faq" from-blog />
      <!-- Related Content Section -->
      <RelatedContent
        v-if="entry.fields.related && entry.fields.related.length"
        :references="entry.fields.related"
      />
      <!-- Footer Links Section -->
      <div>
        <hr class="w-full h-px pt-px mt-12 bg-gray-200 border-0" />
        <div class="mt-8">
          Intéressé par plus d'astuces et de conseils ? Vérifier
          <router-link class="text-gray-900 underline" :to="seriesTag.to">
            {{ seriesTag.title }} </router-link
          >.
        </div>
        <div class="mt-6">
          <router-link
            :to="{ name: 'BlogHome' }"
            class="inline-flex items-center text-base text-blue-600 font-medium hover:underline"
          >
            <svg
              class="w-3 h-3 fill-current text-blue-400 shrink-0 mr-2"
              viewBox="0 0 12 12"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M.293 5.282L5 .5l1.414 1.436-3 3.048H12v2.032H3.414l3 3.048L5 11.5.293 6.718a1.027 1.027 0 010-1.436z"
              />
            </svg>
            <span>Retour au centre de renseignements</span>
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>
